import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaUpload, FaPlay, FaDownload, FaTrash, FaRedo } from 'react-icons/fa';
import api from '../api/axios';
import './Album.css';

// Obtenir l'URL de base de l'API
const API_BASE_URL = (import.meta.env?.VITE_API_URL || process.env.REACT_APP_API_URL || 'http://192.168.1.62:5000/api').replace('/api', '');

const Album = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [album, setAlbum] = useState(null);
  const [media, setMedia] = useState([]);
  const [subAlbums, setSubAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [slideshow, setSlideshow] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideshowSpeed, setSlideshowSpeed] = useState(3000); // 3 secondes par défaut
  const [slideshowPaused, setSlideshowPaused] = useState(false);
  const [imageRefreshKey, setImageRefreshKey] = useState(Date.now());
  const [fullscreenMedia, setFullscreenMedia] = useState(null);
  const [showMediaInfo, setShowMediaInfo] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [availableAlbums, setAvailableAlbums] = useState([]);

  useEffect(() => {
    loadAlbum();
  }, [id]);

  useEffect(() => {
    let interval;
    if (slideshow && media.length > 0 && !slideshowPaused) {
      // Ne démarrer le timer automatique que pour les images
      const currentMedia = media[currentSlide];
      if (currentMedia?.media_type === 'image') {
        interval = setInterval(() => {
          setCurrentSlide((prev) => (prev + 1) % media.length);
        }, slideshowSpeed);
      }
      // Pour les vidéos, l'événement onEnded gère le passage
    }
    return () => clearInterval(interval);
  }, [slideshow, media.length, slideshowSpeed, slideshowPaused, currentSlide]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (fullscreenMedia) {
        if (e.key === 'Escape') {
          closeFullscreen();
        } else if (e.key === 'ArrowRight') {
          navigateFullscreen('next');
        } else if (e.key === 'ArrowLeft') {
          navigateFullscreen('prev');
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [fullscreenMedia, media]);

  const loadAlbum = async () => {
    try {
      const response = await api.get(`/albums/${id}`);
      setAlbum(response.data);
      setMedia(response.data.media || []);
      
      // Charger les sous-albums
      const subAlbumsResponse = await api.get(`/albums?parent_id=${id}`);
      setSubAlbums(subAlbumsResponse.data);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'album', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    console.log('📤 Frontend: Upload de', files.length, 'fichier(s)');
    
    setUploading(true);
    setUploadProgress(0);
    const formData = new FormData();
    
    // IMPORTANT: Ajouter les fichiers un par un avec le même nom de champ
    for (let i = 0; i < files.length; i++) {
      console.log('Ajout fichier', i + 1, ':', files[i].name, files[i].type, '(' + (files[i].size / 1024 / 1024).toFixed(2) + ' MB)');
      formData.append('files', files[i], files[i].name);
    }
    
    // Ajouter l'album_id APRÈS les fichiers
    formData.append('album_id', id);

    try {
      console.log('Envoi de', files.length, 'fichier(s) vers /media/upload...');
      const response = await api.post('/media/upload', formData, {
        headers: { 
          'Content-Type': 'multipart/form-data' 
        },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        timeout: 600000, // 10 minutes
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
          console.log('📊 Progression:', percentCompleted + '%');
        }
      });
      console.log('✅ Upload réussi:', response.data);
      alert(`${files.length} fichier(s) uploadé(s) avec succès !`);
      loadAlbum();
      setUploadProgress(0);
    } catch (error) {
      console.error('❌ Erreur upload:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.error || 'Erreur lors de l\'upload: ' + error.message;
      alert(errorMsg);
      setUploadProgress(0);
    } finally {
      setUploading(false);
      // Réinitialiser l'input file
      e.target.value = '';
    }
  };

  const handleRotate = async (mediaId, degrees) => {
    try {
      await api.patch(`/media/${mediaId}/rotate`, { degrees });
      // Changer la clé pour forcer le rechargement de toutes les images
      setImageRefreshKey(Date.now());
      await loadAlbum();
    } catch (error) {
      alert('Erreur lors de la rotation');
    }
  };

  const handleDownload = async (mediaId) => {
    try {
      const response = await api.get(`/media/${mediaId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'photo.jpg';
      link.click();
    } catch (error) {
      alert('Erreur lors du téléchargement');
    }
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce média ?')) return;

    try {
      await api.delete(`/media/${mediaId}`);
      loadAlbum();
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const handleCreateSubAlbum = async (name) => {
    try {
      await api.post('/albums', {
        name,
        description: '',
        parent_id: id
      });
      loadAlbum(); // Recharger pour afficher le nouveau sous-album
      alert(`Sous-album "${name}" créé avec succès !`);
    } catch (error) {
      alert('Erreur lors de la création du sous-album');
    }
  };

  const openFullscreen = (mediaItem) => {
    setFullscreenMedia(mediaItem);
  };

  const closeFullscreen = () => {
    setFullscreenMedia(null);
  };

  const navigateFullscreen = (direction) => {
    const currentIndex = media.findIndex(m => m.id === fullscreenMedia.id);
    let newIndex;
    
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % media.length;
    } else {
      newIndex = (currentIndex - 1 + media.length) % media.length;
    }
    
    setFullscreenMedia(media[newIndex]);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
  };

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedMedia([]);
  };

  const toggleMediaSelection = (mediaId) => {
    if (selectedMedia.includes(mediaId)) {
      setSelectedMedia(selectedMedia.filter(id => id !== mediaId));
    } else {
      setSelectedMedia([...selectedMedia, mediaId]);
    }
  };

  const selectAll = () => {
    setSelectedMedia(media.map(m => m.id));
  };

  const deselectAll = () => {
    setSelectedMedia([]);
  };

  const loadAvailableAlbums = async () => {
    try {
      const response = await api.get('/albums');
      // Exclure l'album actuel
      setAvailableAlbums(response.data.filter(album => album.id !== parseInt(id)));
    } catch (error) {
      console.error('Erreur chargement albums', error);
    }
  };

  const handleMoveMedia = async (targetAlbumId) => {
    if (selectedMedia.length === 0) {
      alert('Aucun média sélectionné');
      return;
    }

    try {
      await api.post('/media/move', {
        media_ids: selectedMedia,
        target_album_id: targetAlbumId
      });

      alert(`${selectedMedia.length} média(s) déplacé(s) avec succès !`);
      setShowMoveDialog(false);
      setSelectionMode(false);
      setSelectedMedia([]);
      loadAlbum();
    } catch (error) {
      console.error('Erreur déplacement:', error);
      alert('Erreur lors du déplacement des médias');
    }
  };

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  // Mode plein écran
  if (fullscreenMedia) {
    const handleVideoEndFullscreen = () => {
      // Passer automatiquement à la suivante quand la vidéo se termine
      navigateFullscreen('next');
    };
    
    return (
      <div className="fullscreen-viewer" onClick={closeFullscreen}>
        <button className="close-fullscreen" onClick={closeFullscreen}>
          × Fermer
        </button>
        
        <button 
          className="info-toggle-btn" 
          onClick={(e) => { e.stopPropagation(); setShowMediaInfo(!showMediaInfo); }}
          title="Informations"
        >
          ℹ️ Info
        </button>
        
        {media.length > 1 && (
          <>
            <button 
              className="fullscreen-nav fullscreen-nav-prev" 
              onClick={(e) => { e.stopPropagation(); navigateFullscreen('prev'); }}
            >
              ◀
            </button>
            <button 
              className="fullscreen-nav fullscreen-nav-next" 
              onClick={(e) => { e.stopPropagation(); navigateFullscreen('next'); }}
            >
              ▶
            </button>
          </>
        )}

        <div className="fullscreen-content" onClick={(e) => e.stopPropagation()}>
          {fullscreenMedia.media_type === 'image' ? (
            <img 
              src={`${API_BASE_URL}/${fullscreenMedia.file_path}?t=${imageRefreshKey}`}
              alt={fullscreenMedia.original_name}
            />
          ) : (
            <video 
              controls 
              autoPlay
              onEnded={handleVideoEndFullscreen}
              key={fullscreenMedia.id}
            >
              <source src={`${API_BASE_URL}/${fullscreenMedia.file_path}?t=${imageRefreshKey}`} />
            </video>
          )}
        </div>

        <div className="fullscreen-info">
          <p className="fullscreen-filename">{fullscreenMedia.original_name}</p>
          <div className="fullscreen-metadata">
            <span>{fullscreenMedia.media_type === 'image' ? '📷 Image' : '🎬 Vidéo'}</span>
            {fullscreenMedia.width && fullscreenMedia.height && (
              <span>• {fullscreenMedia.width} × {fullscreenMedia.height}px</span>
            )}
            {fullscreenMedia.file_size && (
              <span>• {formatFileSize(fullscreenMedia.file_size)}</span>
            )}
            <span>• {new Date(fullscreenMedia.uploaded_at).toLocaleDateString('fr-FR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</span>
          </div>
          <p className="fullscreen-counter">
            {media.findIndex(m => m.id === fullscreenMedia.id) + 1} / {media.length}
          </p>
        </div>

        {showMediaInfo && (
          <div className="media-info-panel" onClick={(e) => e.stopPropagation()}>
            <h3>📋 Informations détaillées</h3>
            
            <div className="info-section">
              <h4>Fichier</h4>
              <div className="info-row">
                <span className="info-label">Nom:</span>
                <span className="info-value">{fullscreenMedia.original_name}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Nom système:</span>
                <span className="info-value">{fullscreenMedia.filename}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Type:</span>
                <span className="info-value">{fullscreenMedia.mime_type}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Taille:</span>
                <span className="info-value">{formatFileSize(fullscreenMedia.file_size)}</span>
              </div>
            </div>

            {fullscreenMedia.media_type === 'image' && (
              <div className="info-section">
                <h4>Image</h4>
                <div className="info-row">
                  <span className="info-label">Dimensions:</span>
                  <span className="info-value">
                    {fullscreenMedia.width} × {fullscreenMedia.height} pixels
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Ratio:</span>
                  <span className="info-value">
                    {fullscreenMedia.width && fullscreenMedia.height 
                      ? (fullscreenMedia.width / fullscreenMedia.height).toFixed(2) + ':1'
                      : 'N/A'}
                  </span>
                </div>
                {fullscreenMedia.orientation !== 0 && (
                  <div className="info-row">
                    <span className="info-label">Rotation:</span>
                    <span className="info-value">{fullscreenMedia.orientation}°</span>
                  </div>
                )}
              </div>
            )}

            <div className="info-section">
              <h4>Métadonnées</h4>
              <div className="info-row">
                <span className="info-label">Upload:</span>
                <span className="info-value">
                  {new Date(fullscreenMedia.uploaded_at).toLocaleString('fr-FR', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  })}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">ID média:</span>
                <span className="info-value">#{fullscreenMedia.id}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Chemin:</span>
                <span className="info-value info-path">{fullscreenMedia.file_path}</span>
              </div>
            </div>

            <button className="close-info-btn" onClick={() => setShowMediaInfo(false)}>
              Fermer
            </button>
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  if (slideshow) {
    const currentMedia = media[currentSlide];
    
    const handleVideoEnd = () => {
      // Passer automatiquement à la suivante quand la vidéo se termine
      setCurrentSlide((prev) => (prev + 1) % media.length);
    };
    
    return (
      <div className="slideshow-container">
        <button className="close-slideshow" onClick={() => setSlideshow(false)}>
          × Fermer
        </button>
        
        <div className="slideshow-speed-control">
          <label>⏱️ Vitesse:</label>
          <select 
            value={slideshowSpeed} 
            onChange={(e) => setSlideshowSpeed(Number(e.target.value))}
          >
            <option value={1000}>Rapide (1s)</option>
            <option value={2000}>Normale (2s)</option>
            <option value={3000}>Moyenne (3s)</option>
            <option value={5000}>Lente (5s)</option>
            <option value={10000}>Très lente (10s)</option>
          </select>
        </div>

        <div className="slideshow-content">
          {currentMedia?.media_type === 'image' ? (
            <img 
              src={`${API_BASE_URL}/${currentMedia.file_path}?t=${imageRefreshKey}`}
              alt={currentMedia.original_name}
            />
          ) : (
            <video 
              controls 
              autoPlay
              onEnded={handleVideoEnd}
              key={currentSlide}
            >
              <source src={`${API_BASE_URL}/${currentMedia.file_path}?t=${imageRefreshKey}`} />
            </video>
          )}
        </div>
        <div className="slideshow-controls">
          <button onClick={() => setCurrentSlide((prev) => (prev - 1 + media.length) % media.length)}>
            ◀ Précédent
          </button>
          <button 
            className="slideshow-play-pause"
            onClick={() => setSlideshowPaused(!slideshowPaused)}
          >
            {slideshowPaused ? '▶ Play' : '⏸ Pause'}
          </button>
          <span>{currentSlide + 1} / {media.length}</span>
          <button onClick={() => setCurrentSlide((prev) => (prev + 1) % media.length)}>
            Suivant ▶
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="album-page">
      <div className="album-header">
        <button className="btn-back" onClick={() => navigate('/')}>
          <FaArrowLeft /> Retour
        </button>
        <div className="album-title">
          <h1>{album.name}</h1>
          <p>{album.description}</p>
        </div>
        <div className="album-actions">
          {!selectionMode ? (
            <>
              <button 
                className="btn-select" 
                onClick={toggleSelectionMode}
                title="Sélectionner des médias"
              >
                ☑️ Sélectionner
              </button>
              <button 
                className="btn-create-sub" 
                onClick={() => {
                  const name = prompt('Nom du sous-album:');
                  if (name) handleCreateSubAlbum(name);
                }}
              >
                📁 Sous-album
              </button>
              <label className="btn-upload">
                <FaUpload /> {uploading ? `Upload... ${uploadProgress}%` : 'Upload'}
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleUpload}
                  disabled={uploading}
                  style={{ display: 'none' }}
                />
              </label>
              {media.length > 0 && (
                <button className="btn-slideshow" onClick={() => setSlideshow(true)}>
                  <FaPlay /> Diaporama
                </button>
              )}
            </>
          ) : (
            <>
              <button className="btn-select-all" onClick={selectAll}>
                ☑️ Tout
              </button>
              <button className="btn-deselect" onClick={deselectAll}>
                ☐ Aucun
              </button>
              <button 
                className="btn-move" 
                onClick={() => {
                  loadAvailableAlbums();
                  setShowMoveDialog(true);
                }}
                disabled={selectedMedia.length === 0}
              >
                📂 Déplacer ({selectedMedia.length})
              </button>
              <button className="btn-cancel" onClick={toggleSelectionMode}>
                ✕ Annuler
              </button>
            </>
          )}
        </div>
      </div>

      {/* Barre de progression */}
      {uploading && uploadProgress > 0 && (
        <div className="upload-progress-container">
          <div className="upload-progress-bar">
            <div 
              className="upload-progress-fill" 
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="upload-progress-text">
            Upload en cours... {uploadProgress}%
          </p>
        </div>
      )}

      {/* Afficher les sous-albums */}
      {subAlbums.length > 0 && (
        <div className="sub-albums-section">
          <h2>📂 Sous-albums ({subAlbums.length})</h2>
          <div className="sub-albums-grid">
            {subAlbums.map(subAlbum => (
              <div 
                key={subAlbum.id} 
                className="sub-album-card"
                onClick={() => navigate(`/album/${subAlbum.id}`)}
              >
                <div className="sub-album-icon">📁</div>
                <div className="sub-album-info">
                  <h3>{subAlbum.name}</h3>
                  <p>{subAlbum.media_count} médias</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="media-grid">
        {media.map(item => (
          <div 
            key={item.id} 
            className={`media-item ${selectedMedia.includes(item.id) ? 'selected' : ''}`}
          >
            {selectionMode && (
              <div 
                className="selection-checkbox"
                onClick={() => toggleMediaSelection(item.id)}
              >
                <input 
                  type="checkbox" 
                  checked={selectedMedia.includes(item.id)}
                  readOnly
                />
              </div>
            )}
            <div 
              className="media-preview" 
              onClick={() => selectionMode ? toggleMediaSelection(item.id) : openFullscreen(item)}
              style={{ cursor: 'pointer' }}
            >
              {item.media_type === 'image' ? (
                <img 
                  src={`${API_BASE_URL}/${item.thumbnail_path || item.file_path}?t=${imageRefreshKey}`}
                  alt={item.original_name}
                />
              ) : (
                <video>
                  <source src={`${API_BASE_URL}/${item.file_path}?t=${imageRefreshKey}`} />
                </video>
              )}
            </div>
            {!selectionMode && (
              <div className="media-actions">
                {item.media_type === 'image' && (
                  <button 
                    onClick={() => handleRotate(item.id, 90)}
                    title="Pivoter"
                  >
                    <FaRedo />
                  </button>
                )}
                <button 
                  onClick={() => handleDownload(item.id)}
                  title="Télécharger"
                >
                  <FaDownload />
                </button>
                <button 
                  onClick={() => handleDelete(item.id)}
                  title="Supprimer"
                  className="btn-danger"
                >
                  <FaTrash />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {media.length === 0 && (
        <div className="empty-state">
          <p>Aucun média dans cet album</p>
          <label className="btn-upload">
            <FaUpload /> Uploader des fichiers
            <input
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={handleUpload}
              style={{ display: 'none' }}
            />
          </label>
        </div>
      )}

      {/* Dialog de déplacement */}
      {showMoveDialog && (
        <div className="move-dialog-overlay" onClick={() => setShowMoveDialog(false)}>
          <div className="move-dialog" onClick={(e) => e.stopPropagation()}>
            <h2>📂 Déplacer vers...</h2>
            <p className="move-info">
              {selectedMedia.length} média(s) sélectionné(s)
            </p>
            
            <div className="albums-list">
              {availableAlbums.length === 0 ? (
                <p className="no-albums">Aucun autre album disponible</p>
              ) : (
                availableAlbums.map(album => (
                  <div 
                    key={album.id}
                    className="album-option"
                    onClick={() => handleMoveMedia(album.id)}
                  >
                    <div className="album-option-icon">📁</div>
                    <div className="album-option-info">
                      <h3>{album.name}</h3>
                      <p>{album.media_count} médias</p>
                    </div>
                  </div>
                ))
              )}
            </div>

            <button 
              className="btn-cancel-move" 
              onClick={() => setShowMoveDialog(false)}
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Album;